/**
 * Reputation axis scale — the loyalty/method bound shared by the emergent
 * reputation compass (ReputationCard/ReputationCompass) and the diplomacy
 * alignment gauge (AlignmentGauge/NegotiationsPanel). MUST mirror the
 * backend's `Reputation::SCALE` (api/src/Domain/Reputation/Reputation.php).
 * A drift here silently pins high-magnitude captains off the plot — the
 * exact bug this module fixes (réputation vivante widened ±500 → ±1500).
 */
export const REPUTATION_SCALE = 1500

/**
 * Maps a value on [-scale, +scale] to a [0, 100] percentage, CLAMPED so it
 * can never place a dot/tick outside its container — even if the backend
 * scale widens again or a decay/rounding glitch pushes a value past the
 * bound.
 */
export function scaleToPercent(value: number, scale: number = REPUTATION_SCALE): number {
  const pct = ((value + scale) / (2 * scale)) * 100
  return Math.max(0, Math.min(100, pct))
}

/**
 * ReputationCompass plot position: method+ = warrior = right, loyalty+ =
 * lawful = top. Both axes clamped to [0, 100]% via {@link scaleToPercent}.
 */
export function compassPosition(loyalty: number, method: number): { x: number; y: number } {
  return {
    x: scaleToPercent(method),
    y: scaleToPercent(-loyalty),
  }
}

/**
 * Same mapping as {@link scaleToPercent}, nudged into [2, 98]% so the
 * AlignmentGauge's dot/ticks never ride the gauge's rounded end-caps.
 */
export function alignmentGaugePercent(value: number, scale: number = REPUTATION_SCALE): number {
  return Math.max(2, Math.min(98, scaleToPercent(value, scale)))
}
